import { z } from 'zod';

// Hotel, catering and nametags of an in-person event (/api/events/:id/hotel|catering|nametags.csv,
// /api/events/invitees/:id/room|dinners|name-correction). Shape only: nights, dates, meals and
// audiences are cleaned by lib/eventHotel.ts and lib/eventCatering.ts, which keep their messages.

const UUID = /^[0-9a-f-]{36}$/i;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

const Id = z.string().regex(UUID, 'is not an id');
const Day = z.string().regex(DAY, 'is a date (YYYY-MM-DD)');
const Str = z.string().nullish();
const Flag = z.boolean().optional();
/** Cleaned further by the service. */
const Free = z.unknown().optional();

export const EventHotelIncidentals = z.enum(['guest', 'master']);
export type EventHotelIncidentals = z.infer<typeof EventHotelIncidentals>;

export const EventCateringDayParams = z.strictObject({ id: Id, date: Day });
export type EventCateringDayParams = z.input<typeof EventCateringDayParams>;

/** ?hotel=alternate for the overflow hotel; anything else is the main one. */
export const EventHotelWhichQuery = z.strictObject({ hotel: z.string().optional() });
export type EventHotelWhichQuery = z.input<typeof EventHotelWhichQuery>;

/** PUT /api/events/:id/hotel/settings; only the fields sent change. */
export const EventHotelSettingsRequest = z.strictObject({
  name: Str,
  blockSize: z.union([z.number(), z.string(), z.null()]).optional(),
  blockFirstNight: Str,
  blockLastNight: Str,
  blockNights: Free,
  contactEmail: Str,
  checkOutDay: Str,
  incidentals: z.union([EventHotelIncidentals, z.literal('')]).nullish(),
  alternate: Free,
  increaseRequests: Free,
});
export type EventHotelSettingsRequest = z.input<typeof EventHotelSettingsRequest>;

/** POST /api/events/:id/hotel/mark-sent and /catering/mark-sent */
export const EventMarkSentRequest = z.strictObject({ digest: Str, note: Str });
export type EventMarkSentRequest = z.input<typeof EventMarkSentRequest>;

/** POST /api/events/:id/hotel/returned (base64 .xlsx or CSV; read by the service) */
export const EventHotelReturnedRequest = z.strictObject({
  fileName: Str,
  content: z.string().nullish(),
  confirm: Flag,
});
export type EventHotelReturnedRequest = z.input<typeof EventHotelReturnedRequest>;

/** PATCH /api/events/invitees/:inviteeId/room; only the fields sent change. */
export const EventRoomRequest = z.strictObject({
  checkIn: Str,
  checkOut: Str,
  none: z.boolean().nullish(),
  hotel: Str,
  roomType: Str,
  guest: Str,
  sharesWith: Str,
  assistantEmail: Str,
  notes: Str,
  confirmed: z.boolean().nullish(),
  confirmation: Str,
});
export type EventRoomRequest = z.input<typeof EventRoomRequest>;

/** POST /api/events/:id/hotel/returned/accept; keys are the people's row keys on the hotel view. */
export const EventHotelAcceptRequest = z.strictObject({ inviteeIds: z.array(z.string()).nullish(), all: Flag });
export type EventHotelAcceptRequest = z.input<typeof EventHotelAcceptRequest>;

/** POST /api/events/:id/hotel/returned/link; line is the hotel row's key without "hotel:". */
export const EventHotelLinkRequest = z.strictObject({
  line: z.union([z.string(), z.number()]),
  inviteeId: z.string(),
});
export type EventHotelLinkRequest = z.input<typeof EventHotelLinkRequest>;

/** POST /api/events/invitees/:inviteeId/name-correction */
export const EventNameCorrectionRequest = z.strictObject({ use: z.enum(['hotel', 'ours']) });
export type EventNameCorrectionRequest = z.input<typeof EventNameCorrectionRequest>;

/** PUT /api/events/:id/catering/settings; only the fields sent change. */
export const EventCateringSettingsRequest = z.strictObject({
  meals: Free,
  caterer: Free,
  dinnerChoices: Free,
  mealAudiences: Free,
  extra: Free,
  runSheet: Free,
});
export type EventCateringSettingsRequest = z.input<typeof EventCateringSettingsRequest>;

/** PATCH /api/events/invitees/:inviteeId/dinners; a date set to null clears that dinner. */
export const EventDinnersRequest = z.strictObject({
  dinners: z.record(z.string(), z.record(z.string(), z.unknown()).nullable()),
});
export type EventDinnersRequest = z.input<typeof EventDinnersRequest>;
